// ============================================================
// PILLAR 4: Enhanced Integration Center Component
// MedSpaBookings OS - Complete Integration UI
// ============================================================

import React, { useState } from 'react';
import { LockKeyhole, Download, Play, X, FileText, AlertCircle, CheckCircle2, Settings, Shield } from 'lucide-react';
import { StripeConfigurationPanel } from './StripeConfigurationPanel';
import ConnectionsOverview from './integrations/ConnectionsOverview';
import MetaLeadAdsPanel from './integrations/MetaLeadAdsPanel';

// Credential Format Guide
const CREDENTIAL_GUIDES = {
  sendgrid: {
    fields: [
      { label: 'API Key', placeholder: 'SG.xxxxxx...', type: 'password' },
      { label: 'From Email', placeholder: 'noreply@medspabookings.co', type: 'text' }
    ],
    notes: 'from_email: [your-email]'
  },
  twilio: {
    fields: [
      { label: 'Account SID | Auth Token | From Phone', placeholder: 'ACxxxxx|xxxxx|+1234567890', type: 'password' }
    ],
    notes: 'Format: account_sid|auth_token|from_phone (pipe-delimited)'
  },
  outscraper: {
    fields: [
      { label: 'API Key', placeholder: 'Your Outscraper API Key', type: 'password' }
    ],
    notes: ''
  },
  google_workspace: {
    fields: [
      { label: 'Client ID | Client Secret', placeholder: 'xxxxx.apps.googleusercontent.com|xxxxx', type: 'password' }
    ],
    notes: 'Format: client_id|client_secret (pipe-delimited)'
  },
  meta: {
    fields: [
      { label: 'App ID | App Secret', placeholder: 'xxxxxx|xxxxxx', type: 'password' }
    ],
    notes: 'Format: app_id|app_secret (pipe-delimited)'
  }
};

function ProviderPill({ label, type = 'default' }) {
  const colors = {
    'Connected': 'var(--success)',
    'Not Connected': 'var(--muted)',
    'Architecture Ready': 'var(--info)',
    'Error': 'var(--destructive)',
    'Disabled': 'var(--muted)',
    'Ready': 'var(--success)',
    'Awaiting Credentials': 'var(--warning)',
    'Needs Reauth': 'var(--warning)',
    'Needs Setup': 'var(--warning)'
  };
  
  return (
    <span style={{
      fontSize: '10px',
      padding: '4px 8px',
      borderRadius: '6px',
      background: colors[label] || 'var(--border)',
      color: '#fff',
      fontWeight: '600',
      textTransform: 'uppercase'
    }}>
      {label}
    </span>
  );
}

function OutscraperScrapeForm({ api, showNotice, refresh, team }) {
  const [form, setForm] = useState({
    query: 'medical spa in Los Angeles',
    limit: 50,
    assigned_user_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runScrape = async () => {
    setLoading(true);
    try {
      const res = await api.post('/integrations/outscraper/scrape-maps', form);
      setResult(res.data);
      showNotice(`Imported ${res.data.inserted_count} new leads, ${res.data.duplicate_count} duplicates skipped`, 'success');
      await refresh('integrations');
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Scraping failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="panel glass-panel">
      <div className="panel-header">
        <h3>Outscraper Lead Import</h3>
        <span>Google Maps scraping with auto-import</span>
      </div>
      
      <label>
        Search Query
        <input
          data-testid="outscraper-query-input"
          value={form.query}
          onChange={(e) => setForm({ ...form, query: e.target.value })}
          placeholder="e.g., medical spa in Los Angeles, CA"
        />
      </label>

      <div className="form-grid">
        <label>
          Max Results
          <input
            type="number"
            value={form.limit}
            onChange={(e) => setForm({ ...form, limit: Number(e.target.value) })}
            min="1"
            max="200"
          />
        </label>

        <label>
          Assign To
          <select
            value={form.assigned_user_id}
            onChange={(e) => setForm({ ...form, assigned_user_id: e.target.value })}
          >
            <option value="">Default (me)</option>
            {team.map(member => (
              <option key={member.id} value={member.id}>{member.name}</option>
            ))}
          </select>
        </label>
      </div>

      <button
        className="primary-btn"
        data-testid="outscraper-scrape-button"
        onClick={runScrape}
        disabled={loading || !form.query}
      >
        <Download size={16} /> {loading ? 'Scraping...' : 'Run Scrape & Import'}
      </button>

      {result && (
        <div className="result-card" style={{
          marginTop: '16px',
          padding: '16px',
          background: 'rgba(212,166,42,0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(212,166,42,0.3)'
        }}>
          <h4>Import Complete</h4>
          <div className="form-grid">
            <div><strong>Total Scraped:</strong> {result.total_scraped}</div>
            <div><strong>New Leads:</strong> {result.inserted_count}</div>
            <div><strong>Duplicates:</strong> {result.duplicate_count}</div>
            <div><strong>Errors:</strong> {result.error_count}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function ErrorLogsViewer({ api, showNotice }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/integrations/error-logs');
      setLogs(res.data.error_logs || []);
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Failed to load error logs', 'error');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="panel glass-panel">
      <div className="panel-header">
        <h3>Integration Error Logs</h3>
        <span>{logs.length} errors recorded</span>
      </div>

      <button className="ghost-btn small" onClick={loadLogs} disabled={loading}>
        {loading ? 'Loading...' : 'Refresh'}
      </button>

      <div className="compact-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {logs.map(log => (
          <div key={log.id} className="compact-row">
            <AlertCircle size={16} color="var(--destructive)" />
            <div>
              <strong>{log.provider} - {log.error_type}</strong>
              <span>{log.error_message} • {new Date(log.created_at).toLocaleString()}</span>
            </div>
          </div>
        ))}
        {!logs.length && (
          <div className="empty-cell">No error logs yet</div>
        )}
      </div>
    </div>
  );
}

function ImportHistoryViewer({ api, showNotice }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get('/integrations/import-history');
      setHistory(res.data.history || []);
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Failed to load import history', 'error');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="panel glass-panel">
      <div className="panel-header">
        <h3>Outscraper Import History</h3>
        <span>{history.length} imports recorded</span>
      </div>

      <button className="ghost-btn small" onClick={loadHistory} disabled={loading}>
        {loading ? 'Loading...' : 'Refresh'}
      </button>

      <div className="compact-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {history.map(item => (
          <div key={item.id} className="compact-row">
            <CheckCircle2 size={16} color="var(--success)" />
            <div>
              <strong>{item.query}</strong>
              <span>
                {item.total_scraped} scraped • {item.inserted_count} imported • {item.duplicate_count} duplicates • {new Date(item.created_at).toLocaleString()}
              </span>
            </div>
          </div>
        ))}
        {!history.length && (
          <div className="empty-cell">No import history yet</div>
        )}
      </div>
    </div>
  );
}

function IntegrationCenterEnhanced({ data, api, refresh, showNotice, user, team }) {
  const providers = data?.providers || [];
  const categories = data?.categories || [];
  const [credential, setCredential] = useState({
    provider_id: 'sendgrid',
    credential_name: 'Primary API Key',
    api_key: '',
    environment: 'production-ready',
    notes: ''
  });
  const [showOutscraper, setShowOutscraper] = useState(false);
  const [showErrorLogs, setShowErrorLogs] = useState(false);
  const [showImportHistory, setShowImportHistory] = useState(false);
  const [showStripeConfig, setShowStripeConfig] = useState(false);
  const [token] = useState(localStorage.getItem('msb_token'));
  const [rotatingId, setRotatingId] = useState(null);
  const [rotateKey, setRotateKey] = useState('');
  const [webhookForm, setWebhookForm] = useState({
    provider_id: 'sendgrid',
    name: '',
    event: '',
    target_url: '',
    enabled: true,
  });

  const addCredential = async () => {
    try {
      await api.post('/integration-center/credentials', credential);
      setCredential({ ...credential, api_key: '' });
      showNotice('API key encrypted, stored, and masked', 'success');
      await refresh('integrations');
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Failed to add credential', 'error');
    }
  };

  const rotateCredential = async (c) => {
    if (!rotateKey) return;
    try {
      await api.put(`/integration-center/credentials/${c.id}`, {
        provider_id: c.provider_id,
        credential_name: c.credential_name,
        api_key: rotateKey,
        environment: c.environment,
        notes: c.notes,
      });
      setRotatingId(null);
      setRotateKey('');
      showNotice('Credential rotated', 'success');
      await refresh('integrations');
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Failed to rotate credential', 'error');
    }
  };

  const removeCredential = async (c) => {
    if (!window.confirm(`Remove the "${c.credential_name}" credential for ${c.provider_id}? Anything using it will stop working until a new key is stored.`)) return;
    try {
      await api.delete(`/integration-center/credentials/${c.id}`);
      showNotice('Credential removed', 'success');
      await refresh('integrations');
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Failed to remove credential', 'error');
    }
  };

  const addWebhook = async () => {
    try {
      await api.post('/integration-center/webhooks', webhookForm);
      setWebhookForm({ ...webhookForm, name: '', event: '', target_url: '' });
      showNotice('Webhook configured', 'success');
      await refresh('integrations');
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Failed to configure webhook', 'error');
    }
  };

  const removeWebhook = async (w) => {
    if (!window.confirm(`Remove the "${w.name}" webhook?`)) return;
    try {
      await api.delete(`/integration-center/webhooks/${w.id}`);
      showNotice('Webhook removed', 'success');
      await refresh('integrations');
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Failed to remove webhook', 'error');
    }
  };

  const testProvider = async (providerId) => {
    try {
      // Check if this is a real integration test provider
      // Lead providers (outscraper/apify/brightdata) use the generic route,
      // which now makes a real authenticated call for them.
      const realTestProviders = ['sendgrid', 'twilio'];
      const endpoint = realTestProviders.includes(providerId)
        ? `/integrations/${providerId}/test`
        : `/integration-center/providers/${providerId}/test`;

      // A failed test is still HTTP 200: the SendGrid/Twilio/Outscraper
      // routes return {success: false, error} and the generic route returns
      // a log with status "Failed" - so a bad key used to show a green
      // "Connection test completed".
      const { data } = await api.post(endpoint);
      if (data?.success === false || data?.status === 'Failed') {
        showNotice(`Connection test failed: ${data.error || data.message || 'check the credentials'}`, 'error');
      } else {
        showNotice('Connection test completed', 'success');
      }
      await refresh('integrations');
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Test failed', 'error');
    }
  };

  const connectOAuth = async (providerId, providerType = null) => {
    try {
      let endpoint = '';
      let body = {};

      if (providerId === 'google_workspace') {
        // Connects the signed-in user's own Google account (see "Connected accounts").
        endpoint = '/integrations/google/connect';
      } else if (providerId === 'meta_leadforms') {
        endpoint = '/integrations/meta/authorize';
        body = { provider_type: 'leadforms' };
      }

      const res = await api.post(endpoint, body);
      window.open(res.data.auth_url, '_blank', 'noopener');
      showNotice('OAuth authorization opened. Complete the flow in the new window.', 'info');
    } catch (err) {
      showNotice(err.response?.data?.detail || 'OAuth authorization failed', 'error');
    }
  };

  const toggleProvider = async (providerId, enabled) => {
    try {
      await api.post(`/integration-center/providers/${providerId}/toggle`, {
        enabled,
        notes: enabled ? 'Enabled from Integration Center' : 'Disabled from Integration Center'
      });
      showNotice(enabled ? 'Integration enabled' : 'Integration disabled', 'success');
      await refresh('integrations');
    } catch (err) {
      showNotice(err.response?.data?.detail || 'Toggle failed', 'error');
    }
  };

  const grouped = categories.map((category) => [
    category,
    providers.filter((provider) => provider.category === category)
  ]).filter(([, items]) => items.length);

  if (user.role !== 'Master Administrator') {
    return (
      <div className="panel glass-panel">
        <h2>Integration Center is Master Admin only.</h2>
      </div>
    );
  }

  const currentGuide = CREDENTIAL_GUIDES[credential.provider_id];
  // Only Google (per-user Calendar/Gmail) and Meta Lead Ads are really integrated.
  // Instagram / Facebook page data and messaging are not built, so they get no Connect button.
  const oauthProviders = ['google_workspace', 'meta_leadforms'];
  const notBuiltProviders = ['instagram_business', 'facebook_pages', 'meta_ads', 'google_business_profile', 'google_analytics'];

  return (
    <div className="page-stack integration-center" data-testid="integration-center-dashboard">
      <section className="hero-panel ceo-hero glass-panel">
        <div>
          <p className="eyebrow">Secure Control Room • Master Admin Only • PILLAR 4 Complete</p>
          <h2>Integration Center</h2>
          <div className="hero-stats">
            <span>Encrypted at rest</span>
            <span>Masked credentials</span>
            <span>Real provider tests</span>
            <span>OAuth ready</span>
          </div>
        </div>
      </section>

      <section className="revenue-stat-grid">
        <div className="stat-tile glass-panel">
          <span>Providers</span>
          <strong>{data?.summary?.providers || 0}</strong>
        </div>
        <div className="stat-tile glass-panel">
          <span>Credentials</span>
          <strong>{data?.summary?.credentials || 0}</strong>
        </div>
        <div className="stat-tile glass-panel">
          <span>Enabled</span>
          <strong>{data?.summary?.enabled || 0}</strong>
        </div>
        <div className="stat-tile glass-panel">
          <span>Connected</span>
          <strong>{data?.summary?.connected || 0}</strong>
        </div>
        <div className="stat-tile glass-panel">
          <span>Errors</span>
          <strong>{data?.summary?.errors || 0}</strong>
        </div>
      </section>

      {/* Real per-user connection status, Google + Meta Lead Ads */}
      <section className="page-stack" data-testid="integration-connections-section">
        <ConnectionsOverview api={api} showNotice={showNotice} />
        <MetaLeadAdsPanel api={api} showNotice={showNotice} />
      </section>

      {/* Action Buttons */}
      <section className="button-row" style={{ marginBottom: '24px', gap: '12px' }}>
        <button
          className="primary-btn"
          data-testid="integration-show-outscraper-button"
          onClick={() => setShowOutscraper(!showOutscraper)}
        >
          <Download size={16} /> Outscraper Import
        </button>
        <button
          className="ghost-btn"
          data-testid="integration-show-error-logs-button"
          onClick={() => setShowErrorLogs(!showErrorLogs)}
        >
          <AlertCircle size={16} /> Error Logs
        </button>
        <button
          className="ghost-btn"
          data-testid="integration-show-import-history-button"
          onClick={() => setShowImportHistory(!showImportHistory)}
        >
          <FileText size={16} /> Import History
        </button>
      </section>

      {/* Conditional Panels */}
      {showStripeConfig && <StripeConfigurationPanel token={token} onClose={() => { setShowStripeConfig(false); refresh('integrations'); }} />}
      {showOutscraper && <OutscraperScrapeForm api={api} showNotice={showNotice} refresh={refresh} team={team} />}
      {showErrorLogs && <ErrorLogsViewer api={api} showNotice={showNotice} />}
      {showImportHistory && <ImportHistoryViewer api={api} showNotice={showNotice} />}

      {/* Credential Vault */}
      <section className="integration-layout">
        <div className="panel glass-panel credential-vault">
          <div className="panel-header">
            <h3>Encrypted Credential Vault</h3>
            <span>Full keys are never displayed after saving</span>
          </div>

          <label>
            Provider
            <select
              data-testid="integration-credential-provider-select"
              value={credential.provider_id}
              onChange={(e) => setCredential({ ...credential, provider_id: e.target.value })}
            >
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} • {p.category}
                </option>
              ))}
            </select>
          </label>

          {currentGuide && (
            <div className="credential-guide" style={{
              padding: '12px',
              background: 'rgba(212,166,42,0.1)',
              borderRadius: '8px',
              marginBottom: '12px',
              fontSize: '13px'
            }}>
              <strong>Required Format:</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                {currentGuide.fields.map((field, idx) => (
                  <li key={idx}>{field.label}: {field.placeholder}</li>
                ))}
              </ul>
              {currentGuide.notes && <p style={{ marginTop: '8px', fontStyle: 'italic' }}>{currentGuide.notes}</p>}
            </div>
          )}

          <label>
            Credential Name
            <input
              data-testid="integration-credential-name-input"
              value={credential.credential_name}
              onChange={(e) => setCredential({ ...credential, credential_name: e.target.value })}
            />
          </label>

          <label>
            API Key / Secret
            <input
              data-testid="integration-api-key-input"
              type="password"
              value={credential.api_key}
              onChange={(e) => setCredential({ ...credential, api_key: e.target.value })}
              placeholder="Paste secret once; it will be encrypted"
            />
          </label>

          <label>
            Notes (optional)
            <input
              value={credential.notes}
              onChange={(e) => setCredential({ ...credential, notes: e.target.value })}
              placeholder={currentGuide?.notes || 'Additional notes'}
            />
          </label>

          <button
            className="primary-btn"
            data-testid="integration-add-key-button"
            disabled={!credential.api_key}
            onClick={addCredential}
          >
            <LockKeyhole size={16} /> Encrypt & Save Key
          </button>

          <div className="masked-list">
            {(data?.credentials || []).map((c) => (
              <div key={c.id} className="masked-row">
                <strong>{c.credential_name}</strong>
                <span>{c.provider_id} • {c.masked_value}</span>
                {rotatingId === c.id ? (
                  <div className="button-row" style={{ marginTop: '8px' }}>
                    <input
                      type="password"
                      data-testid={`integration-rotate-key-input-${c.id}`}
                      value={rotateKey}
                      onChange={(e) => setRotateKey(e.target.value)}
                      placeholder="Paste new secret"
                    />
                    <button
                      className="primary-btn small"
                      data-testid={`integration-rotate-save-button-${c.id}`}
                      disabled={!rotateKey}
                      onClick={() => rotateCredential(c)}
                    >
                      Save New Key
                    </button>
                    <button
                      className="ghost-btn small"
                      onClick={() => { setRotatingId(null); setRotateKey(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="button-row" style={{ marginTop: '8px' }}>
                    <button
                      className="ghost-btn small"
                      data-testid={`integration-rotate-button-${c.id}`}
                      onClick={() => { setRotatingId(c.id); setRotateKey(''); }}
                    >
                      Rotate
                    </button>
                    <button
                      className="ghost-btn small"
                      data-testid={`integration-remove-credential-button-${c.id}`}
                      onClick={() => removeCredential(c)}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Webhooks */}
      <section className="integration-layout">
        <div className="panel glass-panel" data-testid="integration-webhooks-panel">
          <div className="panel-header">
            <h3>Integration Webhooks</h3>
            <span>Configure delivery targets for provider events</span>
          </div>

          <label>
            Provider
            <select
              data-testid="integration-webhook-provider-select"
              value={webhookForm.provider_id}
              onChange={(e) => setWebhookForm({ ...webhookForm, provider_id: e.target.value })}
            >
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} • {p.category}
                </option>
              ))}
            </select>
          </label>

          <label>
            Name
            <input
              data-testid="integration-webhook-name-input"
              value={webhookForm.name}
              onChange={(e) => setWebhookForm({ ...webhookForm, name: e.target.value })}
              placeholder="e.g. Lead created notifier"
            />
          </label>

          <label>
            Event
            <input
              data-testid="integration-webhook-event-input"
              value={webhookForm.event}
              onChange={(e) => setWebhookForm({ ...webhookForm, event: e.target.value })}
              placeholder="e.g. lead.created"
            />
          </label>

          <label>
            Target URL
            <input
              data-testid="integration-webhook-url-input"
              value={webhookForm.target_url}
              onChange={(e) => setWebhookForm({ ...webhookForm, target_url: e.target.value })}
              placeholder="https://..."
            />
          </label>

          <label className="check-row">
            <input
              type="checkbox"
              checked={webhookForm.enabled}
              onChange={(e) => setWebhookForm({ ...webhookForm, enabled: e.target.checked })}
            />
            Enabled
          </label>

          <button
            className="primary-btn"
            data-testid="integration-add-webhook-button"
            disabled={!webhookForm.name || !webhookForm.event || !webhookForm.target_url}
            onClick={addWebhook}
          >
            Configure Webhook
          </button>

          <div className="masked-list">
            {(data?.webhooks || []).map((w) => (
              <div key={w.id} className="masked-row" data-testid={`integration-webhook-row-${w.id}`}>
                <strong>{w.name}</strong>
                <span>{w.provider_id} • {w.event} • {w.target_url}</span>
                <span>{w.enabled ? 'Enabled' : 'Disabled'} • {w.last_delivery_status}</span>
                <div className="button-row" style={{ marginTop: '8px' }}>
                  <button
                    className="ghost-btn small"
                    data-testid={`integration-remove-webhook-button-${w.id}`}
                    onClick={() => removeWebhook(w)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            {!(data?.webhooks || []).length && (
              <div className="empty-cell">No webhooks configured yet</div>
            )}
          </div>
        </div>
      </section>

      {/* Provider Sections */}
      <section className="provider-sections">
        {grouped.map(([category, items]) => (
          <div key={category} className="panel glass-panel provider-section">
            <div className="panel-header">
              <h3>{category}</h3>
              <span>{items.length} providers</span>
            </div>

            <div className="provider-grid">
              {items.map((provider) => {
                const isOAuth = oauthProviders.includes(provider.id);
                const isRealTest = ['sendgrid', 'twilio', 'outscraper'].includes(provider.id);
                const oauthProviderType = provider.id === 'instagram_business' ? 'instagram'
                  : provider.id === 'facebook_pages' ? 'facebook'
                  : provider.id === 'meta_leadforms' ? 'leadforms'
                  : null;

                return (
                  <article key={provider.id} className="provider-card">
                    <div>
                      <strong>{provider.name}</strong>
                      <span>{provider.description}</span>
                    </div>

                    {provider.connection_detail && (
                      <span data-testid={`integration-live-status-${provider.id}`} style={{ fontSize: 12 }}>
                        {provider.connection_detail.connected} connected - {provider.connection_detail.needs_reauth} need reconnect
                        {provider.last_sync_at ? ` - last sync ${new Date(provider.last_sync_at).toLocaleString()}` : ''}
                      </span>
                    )}
                    <div className="provider-status">
                      <ProviderPill label={provider.status} />
                      <ProviderPill label={provider.health} />
                      <ProviderPill label={`${provider.credential_count || 0} keys`} />
                    </div>

                    <div className="button-row">
                      {provider.id === 'stripe' ? (
                        <button
                          className="primary-btn small"
                          data-testid="integration-configure-stripe-button"
                          onClick={() => setShowStripeConfig(true)}
                        >
                          <Shield size={14} /> Secure Configure
                        </button>
                      ) : isOAuth ? (
                        <button
                          className="primary-btn small"
                          data-testid={`integration-oauth-${provider.id}-button`}
                          onClick={() => connectOAuth(provider.id, oauthProviderType)}
                        >
                          <Play size={14} /> {provider.id === 'google_workspace' ? 'Connect my Google account' : 'Connect Meta'}
                        </button>
                      ) : notBuiltProviders.includes(provider.id) || provider.id === 'google_workspace_email' ? (
                        <span data-testid={`integration-not-built-${provider.id}`} style={{ fontSize: 12, color: 'var(--muted)' }}>
                          {provider.id === 'google_workspace_email' ? 'Gmail send-as-self is under Google Workspace; inbound sync is not built' : 'Not built yet'}
                        </span>
                      ) : (
                        <button
                          className="primary-btn small"
                          data-testid={`integration-test-${provider.id}-button`}
                          onClick={() => testProvider(provider.id)}
                        >
                          {isRealTest ? 'Test Connection' : 'Check Readiness'}
                        </button>
                      )}

                      <button
                        className="ghost-btn small"
                        data-testid={`integration-toggle-${provider.id}-button`}
                        onClick={() => toggleProvider(provider.id, !provider.enabled)}
                      >
                        {provider.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* Sync Logs */}
      <section className="integration-logs">
        <div className="panel glass-panel">
          <div className="panel-header">
            <h3>Sync Logs</h3>
            <span>Status monitoring</span>
          </div>
          <div className="compact-list">
            {(data?.sync_logs || []).slice(0, 20).map((log) => (
              <div key={log.id} className="compact-row">
                <Settings size={16} />
                <div>
                  <strong>{log.provider} - {log.sync_type}</strong>
                  <span>{log.message} • {log.status} • {new Date(log.created_at).toLocaleString()}</span>
                </div>
              </div>
            ))}
            {!(data?.sync_logs || []).length && (
              <div className="empty-cell">No sync logs yet</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default IntegrationCenterEnhanced;
